import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  BrandsByApplianceState,
  DeviceCategory,
  DeviceCategoryTypesRecord,
  DeviceFormValue,
  LaptopDevice,
  MobileDevice,
  UI_BasketItem,
} from '@sky/util-models';
import { Observable, of } from 'rxjs';

import { FeatureConfigFacade } from '@common/data-access-feature-config';
import { Appliance } from '@common/util-models';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { AddDeviceStateService } from './add-device-state.service';

@Component({
  selector: 'sky-ui-add-device',
  templateUrl: './add-device.component.html',
  styleUrls: ['./add-device.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddDeviceStateService],
})
export class SkyUiAddDeviceComponent {
  deviceCategoryEnum = DeviceCategory;

  @Input() deviceCategories: string[] = [];
  @Input() deviceTypes: DeviceCategoryTypesRecord = {};
  @Input() set brands(brands: BrandsByApplianceState) {
    this.cs.setBrands(brands);
  }
  @Input() set basketItem(basketItem: UI_BasketItem) {
    this.cs.setBasketItem(basketItem);
    this.cs.setCategory(basketItem.category);
  }
  @Input() set initialForm(initialForm: DeviceFormValue | undefined | null) {
    this.cs.setInitialFormsData(initialForm);
  }
  @Input() set activeAppliance(appliance: Appliance | undefined) {
    this.cs.setActiveAppliance(appliance);
  }

  @Input() isImeiInvalid = false;

  @Input() allImeisInBasket: string[] = [];

  @Input() isPayedDevice = false;
  @Input() hasAllLTIncludedDevices = false;

  @Input() numberOfDevices = 0;

  @Input() isEngineerJourney = false;

  /*
    This is a makeshift solution to address an urgent and specific
    issue until the ESB team resolves the problem with the virtual device.
    Don't get attached to this code, it's just a temporary workaround
    that works but will be removed as soon as possible.
  */
  _isFirstVirtual = false;
  @Input()
  set isFirstVirtual(value: boolean) {
    this._isFirstVirtual = value;

    if (value) {
      this.selectFirstSkyVirtual();
    }
  }

  get isFirstVirtual(): boolean {
    return this._isFirstVirtual;
  }

  @Output() deviceCategorySelect: EventEmitter<Observable<string>> =
    new EventEmitter();
  @Output() deviceTypeChanges = this.cs.deviceTypeValue$;
  @Output() addDevice: EventEmitter<Observable<DeviceFormValue>> =
    new EventEmitter();
  @Output() removeDevice: EventEmitter<UI_BasketItem> = new EventEmitter();
  @Output() imeiValidate: EventEmitter<string> = new EventEmitter();
  @Output() addLater: EventEmitter<void> = new EventEmitter();
  @Output() isDifferentDevice: EventEmitter<boolean> = new EventEmitter();

  constructor(
    readonly cs: AddDeviceStateService,
    private featureConfigFacade: FeatureConfigFacade
  ) {}

  /*
    This is a workaround solution to address an urgent and specific
    issue until the ESB team resolves the problem with the virtual device.
    Don't get attached to this code, it's just a temporary workaround
    that works but will be removed as soon as possible.
  */
  selectFirstSkyVirtual() {
    setTimeout(() => {
      this.deviceCategorySelect.emit(of('Television'));
      this.cs.setCategory('Television');

      setTimeout(() => {
        const selectElement: any = document.querySelector(
          '[name="deviceType"]'
        );
        selectElement.value = 'Q+';
        const event = new Event('change');
        selectElement.dispatchEvent(event);

        for (let i = selectElement.options.length - 1; i >= 0; i--) {
          if (selectElement.options[i].value !== 'Q+') {
            selectElement.remove(i);
          }
        }
      }, 500);
    }, 1000);
  }

  selectCategory(category: string) {
    this.deviceCategorySelect.emit(of(category));
    this.cs.setCategory(category);
  }

  chooseDifferentDevice() {
    this.cs.resetDevice();
    this.isDifferentDevice.emit(true);
  }

  handleAddDevice(isDisabled: boolean) {
    if (isDisabled) return;

    this.addDevice.emit(
      this.cs.deviceFormValue$.pipe(
        take(1),
        withLatestFrom(
          this.cs.category$,
          this.cs.ltFormSubject.asObservable(),
          this.cs.initialFormsData$,
          this.cs.activeAppliance$
        ),
        map(
          ([
            deviceFormValue,
            category,
            lossTheft,
            initialFormsData,
            activeAppliance,
          ]) => {
            return {
              ...deviceFormValue,
              ...(category === this.deviceCategoryEnum.MobilePhone &&
              initialFormsData
                ? {
                    deviceType: MobileDevice.Code,
                    imei: initialFormsData.imei,
                    brand: initialFormsData.brand,
                    phoneModel: initialFormsData.phoneModel,
                  }
                : {}),
              ...(category === this.deviceCategoryEnum.Laptop
                ? { deviceType: LaptopDevice.Code }
                : {}),
              ...(category === this.deviceCategoryEnum.MobilePhone &&
              initialFormsData &&
              lossTheft
                ? { deviceType: activeAppliance?.applianceCodeLt }
                : {}),
            };
          }
        )
      )
    );
  }

  handleRemoveDevice(basketItem: UI_BasketItem) {
    this.removeDevice.emit(basketItem);
  }

  handleIMEISearch(value: string) {
    if (value) {
      this.imeiValidate.next(value);
    }
  }

  getIsEngineer(): Observable<boolean> {
    return this.featureConfigFacade.isEngineerJourney$;
  }
}
